import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { RatingsInsight } from 'apis/merchant-gateway/insights';
import { EntityType } from 'common/types';

import { RatingsChart } from './RatingsChart';
import { RatingsSummary } from './RatingsSummary';
import { InsightResponseWithPreviousInterval } from '../../types';
import ErrorView from '../ErrorView';
import LoadingView from '../LoadingView';
import SectionHeader from '../SectionHeader';
import { LegendChartType } from '../charts/types';

type RatingsBreakdownProps = {
  ratingsInsight: InsightResponseWithPreviousInterval<RatingsInsight>;
  entityType: EntityType;
};

const RatingsSummaryAndChartContainer = styled.div`
  position: relative;
  border: 1px solid ${cssThemedValue('borderPrimary')};
  border-radius: 8px;
  min-height: 417px;
  display: flex;
  flex-direction: column;
`;
const Divider = styled.div`
  border-block-start: 1px solid ${cssThemedValue('borderPrimary')};
`;
const ChartWrapper = styled.div`
  width: 100%;
`;
const HeaderWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const RatingsBreakdown = ({ ratingsInsight, entityType }: RatingsBreakdownProps) => {
  const { formatMessage } = useIntl();

  const ratingsLegends = useMemo(
    () => ({
      positiveRatings: {
        value: formatMessage({ id: 'insights.ratings-and-reviews.positive-ratings' }),
        color: colors.wolt88,
        type: LegendChartType.BAR,
      },
      negativeRatings: {
        value: formatMessage({ id: 'insights.ratings-and-reviews.negative-ratings' }),
        color: colors.orange48,
        type: LegendChartType.BAR,
      },
      averageRating: {
        value: formatMessage({ id: 'insights.ratings-and-reviews.average-rating' }),
        color: colors.strawberry100,
        type: LegendChartType.LINE,
      },
    }),
    [formatMessage],
  );

  return (
    <RatingsSummaryAndChartContainer>
      <HeaderWrapper>
        <SectionHeader
          title={formatMessage({ id: 'insights.ratings-and-reviews.breakdown' })}
          tooltip={formatMessage({ id: 'insights.ratings-and-reviews.tooltip-scale-10' })}
        />
      </HeaderWrapper>
      <Divider />
      {ratingsInsight.isFetching ? (
        <LoadingView />
      ) : ratingsInsight.isError ? (
        <ErrorView onRetry={ratingsInsight.refetch} />
      ) : (
        <>
          <RatingsSummary
            ratingsInsight={ratingsInsight}
            legends={Object.values(ratingsLegends)}
            entityType={entityType}
          />
          <Divider />
          <ChartWrapper>
            <RatingsChart
              legends={ratingsLegends}
              data={ratingsInsight.selected.data?.data.ratings?.split || []}
              summaryKey="insights.ratings-and-reviews.total-reviews"
            />
          </ChartWrapper>
        </>
      )}
    </RatingsSummaryAndChartContainer>
  );
};

export default RatingsBreakdown;
